import React from "react";
import { Helmet } from "react-helmet-async";

const SEOHead = ({
  title,
  description,
  image,
  url,
  type = "website",
  keywords,
}) => {
  // Create comprehensive JSON-LD structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "LEVELSTAIR",
    url: "https://www.levelstair.com",
    logo: "https://exe.levelstair.com/ExeLogo.png",
    sameAs: [
      "https://www.facebook.com/LEVELSTAIR",
      "https://twitter.com/levelstair",
      "https://www.instagram.com/levelstair",
      "https://discord.gg/xauShxdx6u",
      "https://www.youtube.com/@LEVELSTAIR",
    ],
    description:
      "LEVELSTAIR is Sri Lanka's premier game development community focused on education, networking, and promoting local talent in Unity, Unreal Engine, and Godot development.",
    address: {
      "@type": "PostalAddress",
      addressCountry: "Sri Lanka",
      addressRegion: "Western Province",
    },
    foundingDate: "2024",
    memberOf: {
      "@type": "Organization",
      name: "Sri Lankan Tech Community",
    },
    areaServed: {
      "@type": "Country",
      name: "Sri Lanka",
    },
    knowsAbout: [
      "Game Development",
      "Unity 3D",
      "Unreal Engine",
      "Godot Engine",
      "Game Programming",
      "Game Design",
      "Indie Games",
      "Mobile Games",
      "C# Programming",
      "Blueprint Programming",
      "3D Modeling",
      "Game Art",
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "500",
      bestRating: "5",
    },
    offers: {
      "@type": "Offer",
      name: "EXE Game Development Competition",
      description:
        "Sri Lanka's flagship game development competition with prizes, mentorship, and career opportunities",
      price: "0",
      priceCurrency: "LKR",
    },
  };

  // Event structured data for EXE event
  const eventStructuredData = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: "EXE - Sri Lanka's Flagship Game Development Competition",
    startDate: "2025-08-01T09:00:00+05:30",
    endDate: "2025-10-31T18:00:00+05:30",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Sri Lanka",
      address: {
        "@type": "PostalAddress",
        addressCountry: "Sri Lanka",
      },
    },
    image: "https://exe.levelstair.com/ExeLogo.png",
    description:
      "Join EXE by LEVELSTAIR, Sri Lanka's flagship game development competition. Compete across multiple categories for prizes, mentorship, and career opportunities.",
    organizer: {
      "@type": "Organization",
      name: "LEVELSTAIR",
      url: "https://www.levelstair.com",
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "LKR",
      availability: "https://schema.org/InStock",
      validFrom: "2025-01-01"
    },
    audience: {
      "@type": "Audience",
      audienceType: "Game Developers"
    }
  };

  return (
    <Helmet>
      {/* Enhanced SEO Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      {/* Enhanced Geographic SEO for Sri Lanka */}
      <meta name="geo.region" content="LK" />
      <meta name="geo.placename" content="Sri Lanka" />
      <meta name="geo.position" content="7.8731;80.7718" />
      <meta name="ICBM" content="7.8731, 80.7718" />

      {/* Language and Region */}
      <meta name="language" content="en" />
      <meta name="country" content="Sri Lanka" />
      <meta httpEquiv="content-language" content="en-us" />

      {/* Enhanced Content Classification */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="target" content="all" />
      <meta name="audience" content="all" />
      <meta name="coverage" content="worldwide" />

      {/* Enhanced Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="EXE by LEVELSTAIR" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:updated_time" content={new Date().toISOString()} />

      {/* Enhanced Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@levelstair" />
      <meta name="twitter:creator" content="@levelstair" />
      <meta name="twitter:image:alt" content={title} />

      {/* Enhanced Additional SEO */}
      <link rel="canonical" href={url} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      {/* Enhanced Performance and UX */}
      <meta name="theme-color" content="#53f032" />
      <meta name="msapplication-TileColor" content="#53f032" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta
        name="apple-mobile-web-app-status-bar-style"
        content="black-translucent"
      />

      {/* Enhanced Content freshness and indexing */}
      <meta name="revisit-after" content="3 days" />
      <meta
        name="googlebot"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <meta
        name="bingbot"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />

      {/* Enhanced Author and copyright */}
      <meta name="author" content="LEVELSTAIR Team" />
      <meta
        name="copyright"
        content="© 2025 LEVELSTAIR. All rights reserved."
      />
      <meta name="publisher" content="LEVELSTAIR" />

      {/* Enhanced Cache Control */}
      <meta httpEquiv="cache-control" content="public, max-age=31536000" />

      {/* Rich Snippets Support */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* Event Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(eventStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
